import * as React from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ActionLink from 'components/buttons/action-link/ActionLink';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';

import { copyTextToClipboard, stripPrefix } from 'utils/helper/common';
import { __t, applyTranslations } from 'utils/language';

applyTranslations(require('./deliverable-specification.i18n.json'));

type LinkType = {
  description: string,
  url: string
};

type Props = {
  title: string,
  type: 'items' | 'links' | 'text' | 'richtext' | 'link',
  text?: string,
  items?: Array<string>,
  itemsPrefix?: string,
  links?: Array<LinkType>,
  link?: string
};

class DeliverableSpecification extends React.Component<Props> {
  renderTextView = () => {
    const { text } = this.props;

    return (
      <span
        title={text}
        className="text-overflow-ellipsis"
      >

        {text}
      </span>
    );
  };

  renderLinkView = () => {
    const { link, text } = this.props;

    return (
      <div className="text-overflow-ellipsis">
        <ActionLink
          title={text}
          link={link}
          size="normal"
          className="text-overflow-ellipsis"
          color="secondary"
          openInNewTab
        >
          {text}
        </ActionLink>
      </div>
    );
  };

  renderRichTextView = () => {
    const { text } = this.props;

    return (
      <RichTextEditor
        key={text}
        value={text}
      />
    );
  };

  renderItemsView = () => {
    const { items, itemsPrefix } = this.props;

    return (
      <React.Fragment>
        {
          (items || []).map((item, index) => {
            const label = itemsPrefix ? `${itemsPrefix}${stripPrefix(item)}` : item;

            return (
              <span key={`deliverable-specification-item-${index}`}>
                {label}&nbsp;
              </span>
            );
          })
        }
      </React.Fragment>
    );
  };

  renderLinksView = () => {
    const { links, text } = this.props;

    return (
      <div className="deliverable-specification__links">
        {
          (links || []).map((link, index) => (
            <React.Fragment key={`deliverable-specification-link-${index}`}>
              <span className="deliverable-specification__link-description">
                {link.description}
              </span>
              <div className="deliverable-specification__link text-overflow-ellipsis">
                <ActionLink
                  link={link.url}
                  color="secondary"
                  openInNewTab
                >
                  {link.url}
                </ActionLink>
              </div>
            </React.Fragment>
          ))
        }
        {
          isEmpty(links) && (
            <span>
              {text}
            </span>
          )
        }
      </div>
    );
  };

  render() {
    const { title, type, text, items, links } = this.props;

    const copyKey = Math.random();
    const contentClasses = classnames({
      'deliverable-specification__content': true
    });

    if (type === 'items' && (items || []).length === 0) {
      return null;
    }

    if (type === 'text' && !text) {
      return null;
    }

    if (type === 'richtext' && !text) {
      return null;
    }

    const canCopy = Boolean(type !== 'links' || links?.length);

    return (
      <div className="deliverable-specification">
        <div className="deliverable-specification__title">
          {title}
        </div>
        <div
          className={contentClasses}
          data-copy-key={copyKey}
        >
          {canCopy && (
            <div className="deliverable-specification__hover-icon">
              <Tooltip
                message={__t('deliverable_specification.copied')}
                width="micro"
                trigger="click"
              >
                <SVGDynamicIcon
                  height={16}
                  width={16}
                  name="duplicate-custom-fg"
                  customColor="grey"
                  onClick={() => { copyTextToClipboard(`[data-copy-key="${copyKey}"]`); }}
                />
              </Tooltip>
            </div>
          )}
          {type === 'text' && this.renderTextView()}
          {type === 'link' && this.renderLinkView()}
          {type === 'richtext' && this.renderRichTextView()}
          {type === 'items' && this.renderItemsView()}
          {type === 'links' && this.renderLinksView()}
        </div>
      </div>
    );
  }
}

export default DeliverableSpecification;
